import { defineStore } from 'pinia'

import * as AccountService from '@ser/account'

import { shallowRef } from 'vue'
import {getConfigurations} from "@ser/auctioneer/index.js";
import * as AuctioneerService from "@ser/auctioneer/index.js";
import * as SaleInvoiceService from "@ser/sale_invoice/index.js";
import {createAlias, deleteAlias, updateAlias} from "@ser/account";

export const useAccountStore = defineStore('account', {

    state: () => {
        return {
            _foo: 'foo'
        }
    },

    getters: {
        foo: state => state._foo,
    },

    actions: {

        /**
         * list the accounts
         * by default will surface all accounts that a single user has access to view
         * @param {arr} params 
         * @returns 
         */
        async list(params) {
            return AccountService.list(params)
        },

        async find(params) {
            return AccountService.find(params)
        },

        /**
         * return a specific account
         * @param {int} account_id 
         * @returns 
         */
        async show(account_id) {
            return AccountService.show(account_id)
        },

        /**
         * Creates a new account using the provided data.
         *
         * @param {Object} data The data used to create the object.
         *
         * @return {Promise<Object>} A Promise that resolves to the newly created object.
         */
        async create(data) {
            return AccountService.create(data)
        },

        /**
         * Update an account using the provided data.
         *
         * @param {Object} data The data used to create the object.
         *
         * @return {Promise<Object>} A Promise that resolves to the newly created object.
         */
        async update(account_id, data) {
            return AccountService.update(account_id, data)
        },

        /**
         * Checks if a user has a specific role in a given organisation.
         *
         * @param {Object} user - The user object to check.
         * @param {number} organisation_id - The ID of the organisation to check against.
         * @param {string[]} roles - An array of roles to check for.
         * @param {boolean} strict - A flag to indicate if all roles must be found (true) or if any role is enough (false).
         *
         * @returns {boolean} - Returns true if the user has the specified role(s) in the organisation, otherwise false.
         */
        userHasOrganisationRole(user = null, organisation_id = null, roles = [], strict = false) {
            if (!user || !organisation_id || !Array.isArray(roles)) throw 'malformed role request'
            // find the scope organisation
            const organisation = user?.organisations.find(o => o.id === organisation_id)
            // if it doesn't exist we're falsey
            if (!organisation) return false

            return strict
                ? roles.every(role => organisation.user_roles.includes(role))
                : roles.some(role => organisation.user_roles.includes(role))
        },

        /**
         * Retrieve locations associated with a specific auctioneer.
         *
         * @param {string} account_id - The ID of the account for which locations should be retrieved.
         * @returns {Promise<object>} A Promise that resolves with an object containing the locations associated with the specified auctioneer.
         */
        async getAccountLocations(account_id) {
            return AccountService.locations(account_id)
        },

        /**
         * Creates a new location for the specified account.
         *
         * @param {string} account_id - The ID of the account for which the location needs to be created.
         * @param {Object} payload - The data payload containing information about the location.
         * @returns {Promise<Object>} - A Promise that resolves with the newly created location object.
         */
        async createLocation(account_id = null, payload = {}) {
            return AccountService.createLocation(account_id, payload)
        },

        /**
         * Update the location of an account.
         *
         * @param {string} account_id The ID of the account to update.
         * @param {string} location_id The ID of the location to update.
         * @param {object} payload The data to update the location with.
         * @returns {Promise<object>} A promise that resolves to an object representing the updated location.
         */
        async updateLocation(account_id = null, location_id = null, payload = {}) {
            return AccountService.updateLocation(account_id, location_id, payload)
        },

        /**
         * Assigns a location to an account.
         *
         * @param {string} account_id - The ID of the account to which the location will be assigned.
         * @param {string} location_id - The ID of the location that will be assigned to the account.
         * @returns {Promise<object>} - A Promise that resolves to an object representing the result of the assignment operation.
         */
        async assignLocation(account_id, location_id) {
            return AccountService.assignLocation(account_id, location_id)
        },

        async upsertLogo(account_id, data) {
            return AccountService.upsertLogo(account_id, data)
        },

        async listAliases(account_id) {
            return AccountService.listAliases(account_id)
        },

        async updateAlias(account_id, alias_id, data) {
            return AccountService.updateAlias(account_id, alias_id, data)
        },

        async deleteAlias(account_id, alias_id) {
            return AccountService.deleteAlias(account_id, alias_id)
        },

        async createAlias(account_id, data) {
            return AccountService.createAlias(account_id, data)
        },

        async addSeller(account_id, data) {
            return AccountService.addSeller(account_id, data)
        },

        async addBuyer(account_id, data) {
            return AccountService.addBuyer(account_id, data)
        }
    }

})